import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/hooks/useApi';

// ============================================================================
// Types
// ============================================================================

export interface UserProfile {
  userId: string;
  email: string;
  name?: string;
  authProvider?: 'apple' | 'google' | 'email';
  notifications?: {
    emailNewStories: boolean;
    emailStoryUpdates: boolean;
    pushEnabled: boolean;
  };
  reader?: {
    theme: 'light' | 'dark' | 'sepia' | 'system';
    fontSize: 'small' | 'medium' | 'large' | 'extra-large';
  };
  deviceTokens?: { token: string; platform: 'ios' | 'android' }[];
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileRequest {
  name?: string;
  email?: string;
  notifications?: Partial<UserProfile['notifications']>;
  reader?: Partial<UserProfile['reader']>;
}

// ============================================================================
// Query Keys
// ============================================================================

export const profileKeys = {
  all: ['profile'] as const,
  me: () => [...profileKeys.all, 'me'] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch current user's profile
 */
export function useProfile() {
  return useQuery({
    queryKey: profileKeys.me(),
    queryFn: async () => {
      const response = await api.get<UserProfile>('/users/me');
      return response.data;
    },
  });
}

/**
 * Update current user's profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateProfileRequest) => {
      const response = await api.put<UserProfile>('/users/me', data);
      return response.data;
    },
    onSuccess: (data) => {
      // Update the cached profile data
      queryClient.setQueryData(profileKeys.me(), data);
    },
  });
}

/**
 * Delete current user's account
 */
export function useDeleteProfile() {
  return useMutation({
    mutationFn: async () => {
      await api.delete('/users/me');
    },
  });
}
